import React from 'react';
import moment from 'moment';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import capitalize from 'lodash/capitalize';
import tableStyles from '../../../styles/views/components/Table.module.scss';
import styles from '../../../styles/views/pages/ProjectsFeedback/ProjectsFeedback.module.scss';
import ForwardIcon from '../../../../assets/icon-arrow-forward-brown-filled.svg';
import FiberNewIcon from '../../../../assets/fiber_new_icon.svg';
import { UserRole } from '../../../../../shared/enums';

const columns = {
  project: {
    Header: 'Project',
    accessor: 'projectName',
    disableSortBy: true,
  },
  writerDOR: {
    Header: 'WriterDOR',
    accessor: 'writerDOR',
    disableSortBy: true,
    tooltip: 'writerDOR',
  },
  editorDOR: {
    Header: 'EditorDOR',
    accessor: 'editorDOR',
    disableSortBy: true,
    tooltip: 'editorDOR',
  },
  type: {
    Header: 'Type',
    accessor: 'type',
    disableSortBy: true,
    customStyles: {
      textTransform: 'capitalize',
    },
  },
  structure: {
    Header: 'Structure',
    accessor: 'structure',
    disableSortBy: true,
    customStyles: {
      textAlign: 'right',
    },
    customHeaderStyles: {
      justifyContent: 'right',
    },
  },
  quality: {
    Header: 'Quality',
    accessor: 'quality',
    disableSortBy: true,
    customStyles: {
      textAlign: 'right',
    },
    customHeaderStyles: {
      justifyContent: 'right',
    },
  },
  toneOfVoice: {
    Header: 'Tone of Voice',
    accessor: 'toneOfVoice',
    disableSortBy: true,
    customStyles: {
      textAlign: 'right',
    },
    customHeaderStyles: {
      justifyContent: 'right',
    },
  },
  accuracy: {
    Header: 'Accuracy',
    accessor: 'accuracy',
    disableSortBy: true,
    customStyles: {
      textAlign: 'right',
    },
    customHeaderStyles: {
      justifyContent: 'right',
    },
  },
  average: {
    Header: 'Average',
    accessor: 'average',
    disableSortBy: true,
    customStyles: {
      textAlign: 'right',
    },
    customHeaderStyles: {
      justifyContent: 'right',
    },
  },
  lastRated: {
    Header: 'Last rated',
    accessor: 'lastRated',
    customFieldMapping: {
      tableAccessor: 'lastRated',
      documentKey: 'createdOn',
    },
    customStyles: {
      textAlign: 'right',
    },
    customHeaderStyles: {
      justifyContent: 'right',
    },
  },
  expander: {
    id: 'expander',
    Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
      <span
        className={styles.headerArrowWrapper}
        {...getToggleAllRowsExpandedProps()}
        data-tip={isAllRowsExpanded ? 'Collapse all' : 'Expand all'}
      >
        {isAllRowsExpanded ? <ForwardIcon className={styles.arrowUp} /> : <ForwardIcon className={styles.arrowDown} />}
      </span>
    ),

    Cell: ({ row }) => (row.canExpand ? (
      <span className={styles.arrowWrapper}>
        {row.isExpanded ? (
          <ForwardIcon className={styles.arrowUp} />
        ) : (
          <ForwardIcon className={styles.arrowDown} />
        )}
      </span>
    ) : null),
    disableSortBy: true,
    customStyles: {
      width: '2%',
      padding: '0 16px 0 0',
    },
  },
};

function getColumns() {
  return Object.values(columns);
}

function findMarkValue(marks, keyName) {
  return marks.find(({ key }) => key === keyName)?.value || t('notApplicable');
}

const defineAverageRateStyling = (value) => {
  if (value < 3) {
    return styles.red;
  } else if (value < 4) {
    return styles.orange;
  }
  return styles.green;
};

const checkIsRatingNew = (date) => {
  const now = moment();
  const weekBeforeNow = moment().subtract(1, 'weeks');
  if (moment(date).isBetween(weekBeforeNow, now)) return true;

  return false;
};

function getData(ratings) {
  if (!ratings?.length) return [];
  return ratings.map(({
    _id,
    type,
    marks,
    feedbackMessage,
    createdOn,
    totalRating,
    projectId,
    giverRole,
    editorOnDOR,
    writerOnDOR,
    customerOnDOR,
    isProjectDeleted,
  }) => ({
    _id,
    projectName: (!isProjectDeleted && customerOnDOR?.fullName) || t('phrases.deletedProject', { ns: i18next.Namespace.COMMON }),
    editorDOR: editorOnDOR?.fullName || t('phrases.deletedUser', { ns: i18next.Namespace.COMMON }),
    writerDOR: writerOnDOR?.fullName || t('phrases.deletedUser', { ns: i18next.Namespace.COMMON }),
    type: {
      type,
      giverRole: giverRole === UserRole.DEVELOPMENTAL_EDITOR ? t('roles.developmentalEditor', { ns: i18next.Namespace.COMMON }) : giverRole,
    },
    structure: findMarkValue(marks, 'storyStructure'),
    quality: findMarkValue(marks, 'writingQuality'),
    toneOfVoice: findMarkValue(marks, 'toneOfVoice'),
    accuracy: findMarkValue(marks, 'accuracy'),
    average: totalRating,
    lastRated: createdOn,
    feedbackMessage,
    subRows: [{}],
    isNew: checkIsRatingNew(createdOn),
    projectId,
    isProjectDeleted,
  }));
}

function getExcelData(ratings) {
  if (!ratings?.length) return [];
  return ratings.map(({
    type,
    marks,
    feedbackMessage,
    createdOn,
    totalRating,
    giverRole = '',
    editorOnDOR,
    writerOnDOR,
    customerOnDOR,
    isProjectDeleted,
  }) => ({
    projectName: (!isProjectDeleted && customerOnDOR?.fullName) || t('phrases.deletedProject', { ns: i18next.Namespace.COMMON }),
    editorDOR: editorOnDOR?.fullName || t('phrases.deletedUser', { ns: i18next.Namespace.COMMON }),
    writerDOR: writerOnDOR?.fullName || t('phrases.deletedUser', { ns: i18next.Namespace.COMMON }),
    type: capitalize(type),
    ratedBy: capitalize(giverRole === UserRole.DEVELOPMENTAL_EDITOR ? t('roles.developmentalEditor', { ns: i18next.Namespace.COMMON }) : giverRole),
    feedbackMessage,
    structure: findMarkValue(marks, 'storyStructure'),
    quality: findMarkValue(marks, 'writingQuality'),
    toneOfVoice: findMarkValue(marks, 'toneOfVoice'),
    accuracy: findMarkValue(marks, 'accuracy'),
    average: totalRating.toFixed(1),
    lastRated: moment(createdOn).format('DD MMM YYYY'),
  }));
}

function getExcelColumns() {
  const columnInstances = Object.values(columns);
  const typeColumnIndex = columnInstances.findIndex(inst => inst?.Header === 'Type');
  return columnInstances.reduce((acc, inst, ind) => {
    const header = inst?.Header;
    if (isString(header)) {
      acc.push(header);
      if (ind === typeColumnIndex) {
        acc.push('Rated By');
        acc.push('Feedback Message');
      }
    }
    return acc;
  }, []);
}

function renderCell(cell) {
  const { original: rating = {} } = cell?.row || {};
  switch (cell.column.Header) {
    case columns.project.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.highlightedTableData} style={cell.column.customStyles || {}}>{cell.value}</td>
      );
    case columns.writerDOR.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>{cell.value}</td>
      );
    case columns.editorDOR.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>{cell.value}</td>
      );
    case columns.type.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          {cell.value.type}
          <span className={styles.giverRole}>
            {cell.value.giverRole}
          </span>
        </td>
      );
    case columns.structure.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>{cell.value}</td>
      );
    case columns.quality.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>{cell.value}</td>
      );
    case columns.toneOfVoice.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>{cell.value}</td>
      );
    case columns.accuracy.Header:
      return (
        <td
          {...cell.getCellProps()}
          className={[tableStyles.tableData, isNumber(cell.value) ? '' : styles.grayed]}
          style={cell.column.customStyles || {}}
        >
          {cell.value}
        </td>
      );
    case columns.average.Header:
      return (
        <td {...cell.getCellProps()} className={[tableStyles.tableData, styles.average, defineAverageRateStyling(cell.value)].join(' ')} style={cell.column.customStyles || {}}>
          {cell.value?.toFixed(1)}
        </td>
      );
    case columns.lastRated.Header:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          <span className={styles.lastRated}>
            {moment(cell.value).format('DD MMM')}
            {rating.isNew && <FiberNewIcon className={styles.fiberNewIcon} />}
          </span>
        </td>
      );
    default:
      return (
        <td {...cell.getCellProps()} className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          {cell.render('Cell')}
        </td>
      );
  }
}


export default {
  getColumns,
  renderCell,
  getData,
  getExcelData,
  getExcelColumns,
  columnsFixtures: columns,
};
